
import { Scatter } from "vue-chartjs";

const WARNA_ZONA = "rgba(56, 142, 60, 0.16)";
const WARNA_ZONA_GARIS = "#388e3c";
const WARNA_BOR = "#1565c0";
const WARNA_BTO = "#6a1b9a";
const WARNA_BATAS = "#9e9e9e";
const WARNA_RS = "#c62828";
const WARNA_UNIT_IN = "#2e7d32";
const WARNA_UNIT_OUT = "#ef6c00";

const X_MAX_CAP = 15;
const Y_MAX_CAP = 20;
const X_MIN_TAMPIL = 4;

const SUMBU_X = "bj-x";
const SUMBU_Y = "bj-y";
const Y_MIN_TAMPIL = 13;

export default {
  extends: Scatter,
  props: {
    zona: { type: Object, default: null },
    titikRs: { type: Object, default: null },
    titikUnit: { type: Array, default: () => [] }
  },
  computed: {
    batas() {
      const xs = [];
      const ys = [];
      this.semuaTitik().forEach(t => {
        if (t.toi !== null) xs.push(t.toi);
        if (t.alos !== null) ys.push(t.alos);
      });
      const xMax = Math.min(
        X_MAX_CAP,
        Math.max(X_MIN_TAMPIL, Math.ceil(Math.max(0, ...xs)) + 1)
      );
      const yMax = Math.min(
        Y_MAX_CAP,
        Math.max(Y_MIN_TAMPIL, Math.ceil(Math.max(0, ...ys)) + 1)
      );
      return { xMax, yMax };
    },
    chartData() {
      const { xMax, yMax } = this.batas;
      const jepit = t => ({
        x: Math.max(0, Math.min(xMax, t.toi)),
        y: Math.max(0, Math.min(yMax, t.alos)),
        diLuarGrafik: t.toi > xMax || t.alos > yMax,
        nama: t.nama
      });

      const unitIn = [];
      const unitOut = [];
      this.titikUnitValid().forEach(u => {
        const titik = jepit(u);
        (u.di_zona ? unitIn : unitOut).push(titik);
      });

      const rs = this.titikRsValid() ? [jepit(this.rsTitik())] : [];

      return {
        datasets: [
          {
            label: "Daerah efisiensi",
            data: [],
            backgroundColor: WARNA_ZONA,
            borderColor: WARNA_ZONA_GARIS,
            pointRadius: 0,
            showLine: false
          },
          {
            label: "Bangsal — di dalam zona",
            data: unitIn,
            backgroundColor: WARNA_UNIT_IN,
            borderColor: WARNA_UNIT_IN,
            pointRadius: 5,
            pointHoverRadius: 7,
            showLine: false
          },
          {
            label: "Bangsal — di luar zona",
            data: unitOut,
            backgroundColor: WARNA_UNIT_OUT,
            borderColor: WARNA_UNIT_OUT,
            pointStyle: "triangle",
            pointRadius: 6,
            pointHoverRadius: 8,
            showLine: false
          },
          {
            label: "Seluruh Rawat Inap",
            data: rs,
            backgroundColor: WARNA_RS,
            borderColor: "#ffffff",
            borderWidth: 2,
            pointStyle: "rectRot",
            pointRadius: 11,
            pointHoverRadius: 13,
            showLine: false
          }
        ]
      };
    },
    options() {
      const { xMax, yMax } = this.batas;
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: true, position: "bottom" },
        tooltips: {
          callbacks: {
            label(item, data) {
              const ds = data.datasets[item.datasetIndex];
              const p = ds.data[item.index] || {};
              const nama = p.nama ? `${p.nama}: ` : "";
              const ket = p.diLuarGrafik ? " (di luar rentang grafik)" : "";
              return `${nama}TOI ${item.xLabel} hari, ALOS ${item.yLabel} hari${ket}`;
            }
          }
        },
        scales: {
          xAxes: [
            {
              id: SUMBU_X,
              type: "linear",
              position: "bottom",
              scaleLabel: { display: true, labelString: "TOI — Turn Over Interval (hari)" },
              ticks: { min: 0, max: xMax }
            }
          ],
          yAxes: [
            {
              id: SUMBU_Y,
              type: "linear",
              scaleLabel: { display: true, labelString: "ALOS — Average Length of Stay (hari)" },
              ticks: { min: 0, max: yMax }
            }
          ]
        }
      };
    }
  },
  methods: {
    semuaTitik() {
      const out = this.titikUnitValid().slice();
      if (this.titikRsValid()) out.push(this.rsTitik());
      return out;
    },
    titikUnitValid() {
      return (this.titikUnit || [])
        .filter(u => u && u.toi !== null && u.alos !== null && u.toi >= 0)
        .map(u => ({
          toi: u.toi,
          alos: u.alos,
          di_zona: u.di_zona === true,
          nama: `${u.unit_kode} ${u.unit_nama}`
        }));
    },
    titikRsValid() {
      const t = this.titikRs;
      return !!(t && t.toi !== null && t.alos !== null && t.toi >= 0);
    },
    rsTitik() {
      return {
        toi: this.titikRs.toi,
        alos: this.titikRs.alos,
        di_zona: this.titikRs.di_zona === true,
        nama: this.titikRs.label || "Seluruh Rawat Inap"
      };
    },

    ambilGambar(): string | null {
      const chart = this.$data._chart;
      return chart ? chart.toBase64Image() : null;
    },

    plugin() {
      return {
        beforeDraw(chart) {
          const ctx = chart.ctx;
          ctx.save();
          ctx.globalCompositeOperation = "destination-over";
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, chart.width, chart.height);
          ctx.restore();
        },
        beforeDatasetsDraw: chart => this.gambarZona(chart)
      };
    },

    gambarZona(chart) {
      const z = this.zona;
      if (!z || !z.amplop || !z.amplop.x.length) return;

      const xAxis = chart.scales[SUMBU_X];
      const yAxis = chart.scales[SUMBU_Y];
      if (!xAxis || !yAxis) return;

      const ctx = chart.ctx;
      const px = (x, y) => ({
        x: xAxis.getPixelForValue(x),
        y: yAxis.getPixelForValue(y)
      });

      const { x, bawah, atas } = z.amplop;

      ctx.save();
      ctx.beginPath();
      x.forEach((xi, i) => {
        const p = px(xi, atas[i]);
        if (i === 0) ctx.moveTo(p.x, p.y);
        else ctx.lineTo(p.x, p.y);
      });
      for (let i = x.length - 1; i >= 0; i--) {
        const p = px(x[i], bawah[i]);
        ctx.lineTo(p.x, p.y);
      }
      ctx.closePath();
      ctx.fillStyle = WARNA_ZONA;
      ctx.fill();
      ctx.lineWidth = 1.5;
      ctx.strokeStyle = WARNA_ZONA_GARIS;
      ctx.stroke();
      ctx.restore();

      const a = z.ambang;
      this.garis(chart, z.garis.bor_min, WARNA_BOR, `BOR ${a.bor_min}%`, [6, 4]);
      this.garis(chart, z.garis.bor_max, WARNA_BOR, `BOR ${a.bor_max}%`, [6, 4]);
      this.garis(chart, z.garis.bto_min, WARNA_BTO, `BTO ${a.bto_min_tahunan}/th`, [2, 3]);

      this.garisTegak(chart, a.toi_min, `TOI ${a.toi_min}`);
      this.garisTegak(chart, a.toi_max, `TOI ${a.toi_max}`);
      this.garisDatar(chart, a.alos_min, `ALOS ${a.alos_min}`);
      this.garisDatar(chart, a.alos_max, `ALOS ${a.alos_max}`);
    },

    garis(chart, titik, warna, label, dash) {
      if (!titik || titik.length < 2) return;
      const xAxis = chart.scales[SUMBU_X];
      const yAxis = chart.scales[SUMBU_Y];
      const ctx = chart.ctx;

      const p1 = { x: xAxis.getPixelForValue(titik[0].toi), y: yAxis.getPixelForValue(titik[0].alos) };
      const p2 = { x: xAxis.getPixelForValue(titik[1].toi), y: yAxis.getPixelForValue(titik[1].alos) };

      ctx.save();
      ctx.setLineDash(dash || []);
      ctx.strokeStyle = warna;
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      ctx.moveTo(p1.x, p1.y);
      ctx.lineTo(p2.x, p2.y);
      ctx.stroke();
      ctx.setLineDash([]);
      this.label(ctx, label, warna, p2.x, p2.y, xAxis, yAxis);
      ctx.restore();
    },

    garisTegak(chart, nilai, label) {
      const xAxis = chart.scales[SUMBU_X];
      const yAxis = chart.scales[SUMBU_Y];
      if (nilai > xAxis.max) return;
      const ctx = chart.ctx;
      const x = xAxis.getPixelForValue(nilai);
      ctx.save();
      ctx.setLineDash([3, 3]);
      ctx.strokeStyle = WARNA_BATAS;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(x, yAxis.top);
      ctx.lineTo(x, yAxis.bottom);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = WARNA_BATAS;
      ctx.font = "10px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(label, x, yAxis.top - 2);
      ctx.restore();
    },

    garisDatar(chart, nilai, label) {
      const xAxis = chart.scales[SUMBU_X];
      const yAxis = chart.scales[SUMBU_Y];
      if (nilai > yAxis.max) return;
      const ctx = chart.ctx;
      const y = yAxis.getPixelForValue(nilai);
      ctx.save();
      ctx.setLineDash([3, 3]);
      ctx.strokeStyle = WARNA_BATAS;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(xAxis.left, y);
      ctx.lineTo(xAxis.right, y);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = WARNA_BATAS;
      ctx.font = "10px sans-serif";
      ctx.textAlign = "left";
      ctx.fillText(label, xAxis.left + 3, y - 2);
      ctx.restore();
    },

    label(ctx, teks, warna, x, y, xAxis, yAxis) {
      const px = Math.min(Math.max(x, xAxis.left + 4), xAxis.right - 4);
      const py = Math.min(Math.max(y, yAxis.top + 10), yAxis.bottom - 4);
      ctx.fillStyle = warna;
      ctx.font = "bold 10px sans-serif";
      ctx.textAlign = px > xAxis.right - 60 ? "right" : "left";
      ctx.fillText(teks, px, py);
    },

    gambar() {
      this.renderChart(this.chartData, this.options);
    }
  },
  mounted() {
    this.addPlugin(this.plugin());
    this.gambar();
  },
  watch: {
    chartData: { deep: true, handler() { this.gambar(); } },
    zona: { deep: true, handler() { this.gambar(); } }
  }
};
